import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./lib/api";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Directory from "./pages/Directory";
import ProviderProfile from "./pages/ProviderProfile";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TutorDashboard from "./pages/TutorDashboard";
import ProviderDashboard from "./pages/ProviderDashboard";
import Shop from "./pages/Shop";
import TagPublic from "./pages/TagPublic";
import Plans from "./pages/Plans";
import LostPets from "./pages/LostPets";
import Collars from "./pages/Collars";
import LostPoster from "./pages/LostPoster";
import AvesID from "./pages/AvesID";
import BirdProfile from "./pages/BirdProfile";
import Testers from "./pages/Testers";
import Privacy from "./pages/Privacy";
import DeleteAccount from "./pages/DeleteAccount";
import Admin from "./pages/Admin";

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    api.post("/auth/session", { session_id: sessionId })
      .then((r) => {
        setUser(r.data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/app", { replace: true, state: { user: r.data.user } });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [location, navigate, setUser]);

  return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground" data-testid="auth-callback">Autenticando...</div>;
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (location.state?.user) return children;
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Carregando...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (role && user.role !== role && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Detecta session_id durante o render (não em useEffect) para evitar race condition
  if (location.hash?.includes("session_id=")) {
    return (
      <>
        <Navbar />
        <AuthCallback />
      </>
    );
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-[70vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/prestadores" element={<Directory />} />
          <Route path="/prestador/:id" element={<ProviderProfile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro" element={<Register />} />
          <Route path="/loja" element={<Shop />} />
          <Route path="/planos" element={<Plans />} />
          <Route path="/pets-perdidos" element={<LostPets />} />
          <Route path="/coleiras" element={<Collars />} />
          <Route path="/aves" element={<AvesID />} />
          <Route path="/anilha/:code" element={<BirdProfile />} />
          <Route path="/testadores" element={<Testers />} />
          <Route path="/privacidade" element={<Privacy />} />
          <Route path="/excluir-conta" element={<DeleteAccount />} />
          <Route path="/cartaz/:petId" element={<Protected><LostPoster /></Protected>} />
          <Route path="/tag/:code" element={<TagPublic />} />
          <Route path="/app" element={<Protected><TutorDashboard /></Protected>} />
          <Route path="/painel" element={<Protected role="provider"><ProviderDashboard /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
